import React from 'react';
import { BaseCalendar } from './components/BaseCalendar';
import { EconomicEventsCard } from './components/MobileCards/EconomicEventsCard';
import { createEconomicEventsColumns } from './columns/economicEventsColumns';
import { BaseCalendarPageProps, EconomicEventItem } from './types';
import { applyDateFilter, applyGenericFilter } from './utils/filterUtils';
import { COUNTRY_DATA } from 'constants/countriesCurrency';

const transformApiData = (apiData: any[]): any[] => {
  const transformedItems: any[] = [];

  if (!apiData || apiData.length === 0) return transformedItems;

  const countryCodeToName: { [key: string]: string } = {};
  const currencyToCountry: { [key: string]: string } = {};
  const currencyToCountryCode: { [key: string]: string } = {};

  countryCodeToName['EU'] = 'European Union';
  currencyToCountry['EUR'] = 'European Union';
  currencyToCountryCode['EUR'] = 'EU';

  COUNTRY_DATA.forEach((country) => {
    countryCodeToName[country.code] = country.country;

    if (country.currency && country.currency.trim() !== '') {
      if (!currencyToCountry[country.currency]) {
        currencyToCountry[country.currency] = country.country;
        currencyToCountryCode[country.currency] = country.code;
      }
    }
  });

  countryCodeToName['UK'] = 'United Kingdom';
  currencyToCountry['GBP'] = 'United Kingdom';
  currencyToCountryCode['GBP'] = 'GB';

  apiData.forEach((dayData) => {

    if (!dayData.items || !Array.isArray(dayData.items)) {
      console.warn('Invalid day data structure:', dayData);
      return;
    }

    dayData.items.forEach((item: EconomicEventItem) => {
      try {
        const eventDate = new Date(item.d);

        if (isNaN(eventDate.getTime())) {
          console.warn('Invalid date in item:', item.d);
          return;
        }

        const formattedDate = eventDate.toISOString().split('T')[0];

        const countryFromCode = countryCodeToName[item.co];
        const countryFromCurrency = currencyToCountry[item.cu];
        const countryName = countryFromCode || countryFromCurrency || item.co || 'Unknown';

        let displayCountryCode = item.co;

        if (item.co === 'UK') {
          displayCountryCode = 'GB';
        } else if (!countryFromCode && countryFromCurrency) {
          const codeFromCurrency = currencyToCountryCode[item.cu];
          if (codeFromCurrency) {
            displayCountryCode = codeFromCurrency;
          }
        }

        transformedItems.push({
          date: formattedDate,
          time: eventDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }),
          event: item.e || '',
          country: countryName,
          countryCode: displayCountryCode || '',
          currency: item.cu || '',
          previous: item.p || '',
          estimate: item.es || '',
          actual: item.a || '',
          change: item.ch || '',
          impact: item.i || 'Medium',
          changePercentage: item.cp || '',
        });
      } catch (error) {
        console.error('Error transforming item:', item, error);
      }
    });
  });

  return transformedItems;
};

const EconomicEventsCalendarPage: React.FC<BaseCalendarPageProps> = (props) => {
  const columnWidths = [8, 12, 16, 8, 9, 6, 10, 10, 10, 10, 10, 10];

  const transformData = (data: any[]) => {
    return transformApiData(data || []);
  };

  const filterData = (transformedData: any[], filterOptions: any) => {
    let filteredItems = applyDateFilter(transformedData, filterOptions);

    if (!filterOptions.selectedThirdSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedThirdSectionFilters,
        'currency',
      );
    }

    if (!filterOptions.selectedFourthSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedFourthSectionFilters,
        'impact',
        (item, filter) => {
          const impact = item.impact || 'Medium';
          return impact === filter;
        },
      );
    }

    return filteredItems;
  };

  return (
    <BaseCalendar
      {...props}
      symbol={props?.symbol ?? ''}
      screenType="economic-events"
      title="Economic Events Calendar"
      columns={createEconomicEventsColumns}
      columnWidths={columnWidths}
      mobileCard={EconomicEventsCard}
      filterData={filterData}
      transformData={transformData}
      showWatchlistToggle={false}
    />
  );
};

export default EconomicEventsCalendarPage;
